/**
 * The roles a person can hold on a cycle roster or a bet team.
 *
 * Enough to see that a bet has nobody who designs, without becoming a job-title
 * system. Shape Up staffs small teams of a designer and one or two programmers,
 * and that shape is the only thing worth reading off a roster.
 */
export const MEMBER_ROLES = [
  { value: 'lead', label: 'Lead' },
  { value: 'designer', label: 'Designer' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'member', label: 'Member' },
];

export const MEMBER_ROLE_LABELS = MEMBER_ROLES.reduce((map, role) => {
  map[role.value] = role.label;
  return map;
}, {});

// Leads first, then designers, then engineers. Matters because a card only shows
// the first few faces.
const RANK = { lead: 0, designer: 1, engineer: 2, member: 3 };

export const sortMembers = (members = []) => members.slice().sort((a, b) => (
  (RANK[a.role] ?? 9) - (RANK[b.role] ?? 9)
  || String(a.name || '').localeCompare(String(b.name || ''))
));

/**
 * An OrganizationMember row reshaped for the picker, which speaks in userIds
 * rather than membership rows.
 */
export const candidateFromOrgMember = (member) => ({
  userId: member.userId || member.user?.id,
  name: member.user?.name || null,
  email: member.user?.email || null,
  githubLogin: member.user?.githubLogin || null,
  orgRole: member.role,
});

export const initialsFor = (name) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// A small palette of distinct, mid-tone colors that read well behind white
// initials. Kept deliberately short so faces stay tellable apart at a glance.
const AVATAR_COLORS = [
  '#1e88e5', '#8e24aa', '#00897b', '#e53935', '#fb8c00',
  '#3949ab', '#c0ca33', '#00acc1', '#d81b60', '#43a047',
  '#f4511e', '#5e35b1', '#039be5', '#7cb342', '#6d4c41',
];

/**
 * A stable avatar color for a person, hashed from whatever identity is handed
 * in (a userId is best; a name works as a fallback). The same key always maps
 * to the same color, so one person wears one color across the whole board.
 */
export const colorFor = (key) => {
  const str = String(key || '');
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};
